import React, { useState, useEffect, useCallback, useRef, memo } from 'react';

import { showFullScreenError } from '../../../store/actions/error';
import { useFeaturedContentRowQuery } from '../../../client/query/featured-content';
import { HybridConsoleModePicker } from './hybrid-console-mode-picker';
import Rows from './rows';
import { showButton, hideModality } from '../../../store/to-native';
import {
  upgradeButtonText,
  freeSignUpButtonText,
} from '../../../i18n/translations';
import throttle from 'lodash.throttle';
import LibraryFeaturedContent from '../../../components/library-featured-content';
import { getSearchState } from '../../search/state/search-state';
import { useMembership } from '../../../client/query/users';
import { useDispatch } from 'react-redux';
import { useConfig } from '../../../store/reducers/config';
import { useDeviceType } from '../../../store/device-type';
import { isMobile } from '../../../helpers/responsive';
import ModalProvider from '../../../components/upgrade-modal/modal.provider';
import UpgradeModal from '../../../components/upgrade-modal';

interface RefObject<T> {
  current: T | null;
}

export const Library: React.FC = () => {
  let freeRef: RefObject<HTMLInputElement>;
  const refHeight = useRef(0);

  const [allSectionsHaveError, setAllSectionsHaveError] = useState(false);
  const membership = useMembership();
  const dispatch = useDispatch();
  const { token, entryPoint } = useConfig();
  const { data, isError } = useFeaturedContentRowQuery();
  const { isFreeLibrary } = membership;

  const checkAllErrored = useCallback(() => {
    if (allSectionsHaveError) {
      return;
    }

    if (isError) {
      setAllSectionsHaveError(true);
      dispatch(showFullScreenError());
    }
  }, [allSectionsHaveError, dispatch, isError]);

  const handleScroll = throttle(() => {
    const message = token ? upgradeButtonText() : freeSignUpButtonText();
    if (window.pageYOffset > refHeight.current && !membership.isPaused) {
      showButton(true, message);
    } else {
      showButton(false);
    }
  }, 1000);

  useEffect(() => {
    checkAllErrored();
    if (isFreeLibrary) {
      refHeight.current = freeRef?.current?.clientHeight ?? 0;
      window.addEventListener('scroll', handleScroll);
      handleScroll();
    }
    return () => {
      if (isFreeLibrary) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [checkAllErrored, freeRef, handleScroll, isFreeLibrary]);

  const freeCompRef = (ref): void => {
    if (!freeRef) {
      freeRef = React.createRef();
      refHeight.current = 0;
    }
    freeRef.current = ref;
  };
  const deviceType = useDeviceType();
  useEffect(() => {
    getSearchState().setViewContext({});
    checkAllErrored();
    if (!isMobile(deviceType)) {
      hideModality();
    }
  }, [checkAllErrored, entryPoint, deviceType]);

  return (
    <ModalProvider>
      <div className="library-view">
        <HybridConsoleModePicker />
        {(!isError || isFreeLibrary) && (
          <LibraryFeaturedContent
            featuredItems={data?.featuredItems}
            onRef={membership ? freeCompRef : undefined}
          />
        )}
        <Rows />
      </div>
      <UpgradeModal />
    </ModalProvider>
  );
};

export default memo(Library);
